import request from '@utils/axios-utils';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { ApiList, Document, ApiDetail, CommonHash, ApiObject } from '@types';
import { useAuth } from '@providers/auth';
import Notification from '@components/notification/notification';
import { isDocumentPayable } from '@utils';

const PER_PAGE = 10;

export function useDocuments(
  type: string,
  start_date: string | null,
  end_date: string | null,
  tax_id: string[],
  legal_name: string[],
  document_type: string[],
  status: string[],
  code: string | undefined,
  number: string | undefined,
  external_id: string,
  company_id: string[],
  page: number,
  paid: string,
  key: string,
  direction: string,
  cost_center_id: string | null,
) {
  const { session } = useAuth();
  return useQuery<ApiList<Document>>(
    [
      'documents',
      start_date,
      end_date,
      tax_id,
      legal_name,
      document_type,
      status,
      code,
      number,
      external_id,
      company_id,
      page,
      paid,
      key,
      direction,
      cost_center_id,
    ],
    async () => {
      legal_name = legal_name.map((name) => encodeURIComponent(name));
      const response = await request({
        authorizationToken: session?.token,
        url:
          `/documents` +
          `?type=${type}` +
          `&start_date=${start_date}` +
          `&end_date=${end_date}` +
          `&tax_id=${[...tax_id, ...legal_name]}` +
          `&document_type=${document_type}` +
          `&status=${status}` +
          `&code=${code}` +
          `&number=${number}` +
          `&company_id=${company_id}` +
          `&page=${page}` +
          `&page_size=${PER_PAGE}` +
          `&paid=${paid}` +
          `&key=${key}` +
          `&direction=${direction}` +
          `&like_external_id=${external_id}` +
          `&cost_center_id=${cost_center_id ?? ''}`,
        method: 'GET',
      });
      return response.data;
    },
    {
      enabled: session.status != 'loading',
    },
  );
}
export function useDocument(
  uuid: string | undefined,
  options?: { includeDraft?: boolean },
) {
  const { session } = useAuth();
  const includeDraft = options?.includeDraft === true;
  return useQuery<ApiObject<Document>>(
    ['document_details', uuid, includeDraft],
    async () => {
      const qs = includeDraft ? '?include_draft=true' : '';
      const response = await request({
        authorizationToken: session?.token,
        url: `/documents/${uuid}${qs}`,
        method: 'GET',
      });
      return response.data;
    },
    {
      enabled: session.status != 'loading' && uuid !== '' && uuid !== undefined,
      onSuccess: () => {},
    },
  );
}

export function useNewDocument(uuid: string | undefined) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useQuery<ApiObject<Document>>(
    ['new_document'],
    async () => {
      const response = await request({
        authorizationToken: session?.token,
        url: `/documents/new`,
        method: 'GET',
      });
      return response.data;
    },
    {
      enabled: session.status != 'loading' && uuid === undefined,
      onSuccess: () => {
        queryClient.invalidateQueries(['document_details']);
      },
    },
  );
}

export function downloadXml(token: string, uuid: string) {
  return request({
    authorizationToken: token,
    url: `/documents/${uuid}/raw_xml`,
    method: 'GET',
  });
}

export async function downloadBulkPdf(token: string, uuids: string[]): Promise<void> {
  const response = await request({
    authorizationToken: token,
    url: `/documents/bulk_download_pdf`,
    method: 'POST',
    data: { uuids },
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `facturas_${new Date().toISOString().slice(0, 10)}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function useAnswerDocument() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation(
    ({ data }: CommonHash) => {
      return request({
        authorizationToken: session?.token,
        url: `/documents/${data['id']}/answer`,
        method: 'POST',
        data,
      });
    },
    {
      onSuccess: (data: CommonHash) => {
        if (data.request.status != 200) {
          Notification({ type: 'error', message: 'Error actualizando estado.' });
        } else {
          Notification({ type: 'success', message: 'Estado actualizado con éxito.' });
          queryClient.invalidateQueries(['documents']);
        }
      },
    },
  );
}

export function useVoidDocumentMutation() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation(
    ({ uuid, issueDate }: { uuid: string; issueDate: string }) => {
      return request({
        authorizationToken: session?.token,
        url: `/documents/${uuid}/void_document`,
        method: 'POST',
        data: {
          issue_date: issueDate,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents']);
        Notification({ type: 'success', message: 'Se ha generado el documento.' });
      },
      onError: (error: any) => {
        Notification({
          type: 'error',
          message: 'Error al generar el documento. ' + error.response.data.error.message,
        });
      },
    },
  );
}

export function useCedeDocumentMutation() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation(
    ({ uuid, cession }: { uuid: string; cession: { [key: string]: unknown } }) => {
      return request({
        authorizationToken: session?.token,
        url: `/documents/${uuid}/cede_document`,
        method: 'POST',
        data: {
          cession,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents']);
        Notification({ type: 'success', message: 'Se ha generado el documento.' });
      },
      onError: (error: any) => {
        Notification({
          type: 'error',
          message: 'Error al ceder documento. ' + error.response.data.error.message,
        });
      },
    },
  );
}

export function useResendPDFMutation() {
  const { session } = useAuth();
  return useMutation(
    ({ uuid, contact_email }: { uuid: string; contact_email: string[] }) => {
      return request({
        authorizationToken: session?.token,
        url: `/documents/${uuid}/resend_pdf`,
        method: 'POST',
        data: {
          contact_email,
        },
      });
    },
    {
      onSuccess: () => {
        Notification({ type: 'information', message: 'El PDF llegará en un par de minutos' });
      },
    },
  );
}

export function usePayDocumentMutation() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation(
    ({ uuid, type, status, paid = true, paid_at, payment_method_id }: { uuid: string; type?: string; status?: string; paid?: boolean; paid_at?: string; payment_method_id?: number }) => {
      if (!isDocumentPayable(type, status)) {
        throw new Error('Este tipo de documento no se puede marcar como pagado.');
      }

      return request({
        authorizationToken: session?.token,
        url: `/documents/${uuid}/mark_as_paid`,
        method: 'POST',
        data: { paid, paid_at, payment_method_id },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents']);
        Notification({ type: 'success', message: 'El documento ha sido marcado como pagado.' });
      },
    },
  );
}
export function useBulkMarkAsPaidMutation() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation(
    ({
      uuids,
      paid,
      paid_at,
      payment_method_id,
    }: {
      uuids: string[];
      paid: boolean;
      paid_at?: string;
      payment_method_id?: number;
    }) =>
      request({
        authorizationToken: session?.token,
        url: `/documents/bulk_mark_as_paid`,
        method: 'POST',
        data: { uuids, paid, paid_at, payment_method_id },
      }),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['documents']);
        Notification({
          type: 'success',
          message: variables.paid
            ? 'Documentos marcados como pagados.'
            : 'Documentos marcados como no pagados.',
        });
      },
    },
  );
}

export function useBookDocument(start_date: string, end_date: string, book_type: string) {
  const { session } = useAuth();
  return useQuery<string>(
    ['book', start_date, end_date],
    async () => {
      const response = await request({
        authorizationToken: session?.token,
        url: `/documents/generate_book?book_type=${book_type}&start_date=${start_date}&end_date=${end_date}`,
        method: 'GET',
      });
      const blob = new Blob([response.data], { type: 'text/plain' });
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const name = book_type == 'sales' ? 'LibroVentas' : 'LibroCompras';
      link.href = fileURL;
      link.setAttribute('download', `${name}_${start_date}_${end_date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      return fileURL;
    },
    {
      enabled: false,
    },
  );
}

export function useAddDocument() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation(
    ({ data }: CommonHash) => {
      return request({
        authorizationToken: session?.token,
        url: `/documents`,
        method: 'POST',
        data,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('documents');
      },
      onError: (error: any) => {
        Notification({
          type: 'error',
          message: error.response?.data?.error?.message || 'Error al crear el documento.',
        });
      },
    },
  );
}

export function useDocumentReference(type: string, number: string) {
  const { session } = useAuth();
  return useQuery<ApiDetail<Document>>(
    ['document', type, number],
    async () => {
      const response = await request({
        authorizationToken: session?.token,
        url: `/documents/reference?&type=${type}&number=${number}`,
        method: 'GET',
      });
      return response.data;
    },
    {
      enabled: session.status != 'loading',
    },
  );
}

export function useEmitDocumentMutation() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation(
    ({ uuid }: { uuid: string }) => {
      return request({
        authorizationToken: session?.token,
        url: `/documents/${uuid}/emit`,
        method: 'PATCH',
      });
    },
    {
      onSuccess: () => {
        ['documents', 'file_imports', 'cafs'].forEach((key) =>
          queryClient.invalidateQueries([key]),
        );
        Notification({ type: 'success', message: 'Se ha emitido el documento.' });
      },
      onError: (error: any) => {
        Notification({
          type: 'error',
          message: 'Error al generar el documento. ' + error.response.data.error.message,
        });
      },
    },
  );
}

export function useUpdateDocument() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation(
    ({ uuid, data }: { uuid: string; data: CommonHash }) => {
      return request({
        authorizationToken: session?.token,
        url: `/documents/${uuid}`,
        method: 'PATCH',
        data,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents']);
        queryClient.invalidateQueries(['file_imports']);
        Notification({ type: 'success', message: 'Se ha actualizado el documento.' });
      },
      onError: () => {
        Notification({ type: 'error', message: 'Error al actualizar el documento.' });
      },
    },
  );
}

export function useDeleteDocument() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation(
    ({ uuid }: { uuid: string }) => {
      return request({
        authorizationToken: session?.token,
        url: `/documents/${uuid}`,
        method: 'DELETE',
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents']);
        queryClient.invalidateQueries(['file_imports']);
        Notification({ type: 'success', message: 'Se ha eliminado el documento.' });
      },
    },
  );
}

export function useConvertReceipt() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ uuid, data }: { uuid: string; data: CommonHash }) => {
      return request({
        url: `/documents/${uuid}/convert_receipt`,
        method: 'POST',
        data,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents']);
        Notification({
          type: 'success',
          message: 'Se ha mandando a generar la factura electrónica.',
        });
      },
      onError: () => {
        Notification({ type: 'error', message: 'Error al generar la factura.' });
      },
    },
  );
}

export function useFetchSIIBook() {
  const { session } = useAuth();
  return useMutation(
    ({ book_type, start_month, end_month }: { book_type: string; start_month: string; end_month: string }) => {
      return request({
        authorizationToken: session?.token,
        url: `/documents/fetch_from_sii`,
        method: 'POST',
        data: { book_type, start_month, end_month },
      });
    },
    {
      onSuccess: () => {
        Notification({ type: 'success', message: 'Búsqueda iniciada. Los documentos se importarán en los próximos minutos.' });
      },
      onError: () => {
        Notification({ type: 'error', message: 'Error al iniciar la búsqueda en el SII.' });
      },
    },
  );
}

export function useDocumentUnauthenticated(uuid: string | undefined) {
  const { session } = useAuth();
  return useQuery<ApiObject<Document>>(
    ['document_details', uuid],
    async () => {
      const response = await request({
        url: `/documents/${uuid}/unauthenticated`,
        method: 'GET',
      });
      return response.data;
    },
    {
      enabled: session.status != 'loading' && uuid !== '' && uuid !== undefined,
      onSuccess: () => {},
      onError: (e) => {
        if (e instanceof AxiosError) {
          Notification({ type: 'error', message: e?.response?.data.message });
        }
      },
    },
  );
}

export function useDuplicateDocument() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  return useMutation(
    ({ uuid }: { uuid: string }) => {
      return request({
        authorizationToken: session?.token,
        url: `/documents/${uuid}/duplicate`,
        method: 'POST',
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents']);
        Notification({ type: 'success', message: 'Se ha duplicado el documento.' });
      },
      onError: () => {
        Notification({ type: 'error', message: 'Error al duplicar el documento.' });
      },
    },
  );
}
